import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { ChefHat, LogOut, RefreshCw, Users, Bell, Clock, Utensils, AlertCircle, Globe, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import { getKitchenMeals } from "@/lib/api";
import { kitchenTranslations } from "@/lib/translations";
import { playNotificationSound, isSoundEnabled, toggleSound } from "@/lib/notificationSound";

const REFRESH_INTERVAL = 30000; // 30 seconds

export default function KitchenDashboard() {
  const navigate = useNavigate();
  const [meals, setMeals] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [currentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [roomServiceModal, setRoomServiceModal] = useState({ open: false, mealType: null, mealName: "" });
  const [soundEnabled, setSoundEnabled] = useState(isSoundEnabled);
  const previousTotalRef = useRef(null);
  
  // Default to Spanish, persisted in localStorage
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("kitchenLanguage") || "es";
  });

  const t = kitchenTranslations[language];
  const dateLocale = language === "es" ? es : enUS;

  const toggleLanguage = () => {
    const newLang = language === "es" ? "en" : "es";
    setLanguage(newLang);
    localStorage.setItem("kitchenLanguage", newLang);
  };

  const handleToggleSound = () => {
    const newSetting = toggleSound();
    setSoundEnabled(newSetting);
  };

  // Meal name mapping
  const getMealName = (mealType) => {
    const names = {
      breakfast: t.breakfast,
      lunch: t.lunch,
      dinner: t.dinner
    };
    return names[mealType] || mealType;
  };

  // Dietary mapping
  const getDietaryLabel = (diet) => {
    const labels = {
      vegetarian: t.vegetarian,
      vegan: t.vegan,
      "gluten-free": t.glutenFree
    };
    return labels[diet] || diet;
  };

  // Calculate total reservations
  const getTotalReservations = (mealsData) => {
    if (!mealsData) return 0;
    return Object.values(mealsData).reduce((total, meal) => {
      return total + (meal?.reservations?.length || 0);
    }, 0);
  };

  const loadMeals = useCallback(async (isInitialLoad = false) => {
    try {
      const res = await getKitchenMeals(currentDate);
      const newTotal = getTotalReservations(res.data);
      
      // Play sound if new reservations detected (not on initial load)
      if (!isInitialLoad && previousTotalRef.current !== null && newTotal > previousTotalRef.current && soundEnabled) {
        playNotificationSound();
      }
      
      previousTotalRef.current = newTotal;
      setMeals(res.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error loading meals:", error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, soundEnabled]);

  useEffect(() => {
    // Check auth
    if (sessionStorage.getItem("kitchenAuth") !== "true") {
      navigate("/kitchen");
      return;
    }

    loadMeals(true); // Initial load

    // Set up auto-refresh
    const interval = setInterval(() => loadMeals(false), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [navigate, loadMeals]);

  const handleLogout = () => {
    sessionStorage.removeItem("kitchenAuth");
    navigate("/kitchen");
  };

  const getMealStatus = (startTime) => {
    const now = new Date();
    const [hours, minutes] = startTime.split(":").map(Number);
    const mealTime = new Date();
    mealTime.setHours(hours, minutes, 0, 0);
    
    const diff = mealTime - now;
    const hoursUntil = diff / (1000 * 60 * 60);
    
    if (diff < 0) return { status: "passed", label: t.passed };
    if (hoursUntil <= 1) return { status: "soon", label: t.soon };
    return { status: "upcoming", label: t.upcoming };
  };

  const mealOrder = ["breakfast", "lunch", "dinner"];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <ChefHat className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">{t.loadingKitchen}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="kitchen-dashboard">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold">{t.kitchenPanel}</h1>
                <p className="text-sm text-muted-foreground">La Olla</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm text-muted-foreground">{t.today}</p>
                <p className="font-medium" data-testid="current-date">
                  {language === "es" 
                    ? format(new Date(), "EEEE, d 'de' MMMM", { locale: dateLocale })
                    : format(new Date(), "EEEE, MMMM d", { locale: dateLocale })
                  }
                </p>
              </div>
              
              {/* Sound Toggle */}
              <Button
                variant="outline"
                size="icon"
                onClick={handleToggleSound}
                className={cn("border-border", soundEnabled && "bg-primary/10")}
                data-testid="kitchen-sound-toggle"
                title={soundEnabled ? (language === "es" ? "Silenciar notificaciones" : "Mute notifications") : (language === "es" ? "Activar sonido" : "Enable sound")}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              
              {/* Language Toggle */}
              <Button
                variant="outline"
                size="icon"
                onClick={toggleLanguage}
                className="border-border"
                data-testid="kitchen-language-toggle"
                title={language === "es" ? "Switch to English" : "Cambiar a Español"}
              >
                <Globe className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadMeals(false)}
                className="border-border"
                data-testid="refresh-btn"
                title={t.refresh}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{t.logout}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Last Update */}
      <div className="bg-accent/50 py-2 px-4 text-center">
        <p className="text-sm text-muted-foreground">
          {t.lastUpdate}: {format(lastUpdate, "h:mm:ss a")} • {t.autoRefresh}
        </p>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {mealOrder.map((mealType) => {
            const mealData = meals[mealType];
            if (!mealData) return null;

            const { status, label } = getMealStatus(mealData.start_time);
            
            return (
              <div key={mealType} className="space-y-4" data-testid={`meal-section-${mealType}`}>
                {/* Meal Header Card */}
                <Card className={cn(
                  "bg-card border-border overflow-hidden",
                  status === "soon" && "border-warning"
                )}>
                  <div className={cn(
                    "h-2",
                    status === "passed" && "bg-muted",
                    status === "upcoming" && "bg-info",
                    status === "soon" && "bg-warning animate-pulse"
                  )} />
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-display text-2xl">
                        {getMealName(mealType)}
                      </CardTitle>
                      <Badge
                        variant={status === "passed" ? "secondary" : status === "soon" ? "destructive" : "outline"}
                      >
                        {label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{mealData.display_time || `${mealData.start_time} - ${mealData.end_time}`}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-background/50 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center gap-2 text-2xl font-bold text-primary">
                          <Users className="w-5 h-5" />
                          <span data-testid={`${mealType}-total-guests`}>{mealData.total_guests}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t.totalGuests}</p>
                      </div>
                      <div 
                        className="bg-background/50 rounded-lg p-3 text-center cursor-pointer hover:bg-background/70 transition-colors"
                        onClick={() => {
                          if (mealData.total_room_service > 0) {
                            setRoomServiceModal({ 
                              open: true, 
                              mealType: mealType, 
                              mealName: getMealName(mealType)
                            });
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="flex items-center justify-center gap-2 text-2xl font-bold text-info">
                          <Bell className="w-5 h-5" />
                          <span data-testid={`${mealType}-room-service`}>{mealData.total_room_service}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t.roomService}</p>
                        {mealData.total_room_service > 0 && (
                          <p className="text-xs text-info mt-1">{t.tapToView}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Reservations List */}
                <div className="space-y-2">
                  {mealData.reservations.length === 0 ? (
                    <Card className="bg-card/50 border-border">
                      <CardContent className="py-8 text-center">
                        <Utensils className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">{t.noReservations}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    mealData.reservations.map((res) => (
                      <Card
                        key={res.id}
                        className={cn(
                          "bg-card border-l-4 border-border",
                          res.room_service ? "border-l-info" : "border-l-primary"
                        )}
                        data-testid={`kitchen-reservation-${res.id}`}
                      >
                        <CardContent className="py-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-mono font-medium text-lg">
                                {res.hotel_name}
                              </p>
                              <p className="text-primary font-semibold">
                                {t.cabin}: {res.cabin_name}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {res.guest_name}
                              </p>
                              {res.room_service && (
                                <div className="mt-2 bg-info/20 border border-info rounded-md px-3 py-2 inline-flex items-center gap-2">
                                  <Bell className="w-4 h-4 text-info" />
                                  <span className="text-info font-semibold">{t.roomService}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <Badge variant="secondary" className="text-lg px-3 py-1">
                                <Users className="w-4 h-4 mr-1" />
                                {res.guest_count}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Dietary Requirements & Special Time */}
                          {(res.dietary_requirements || res.special_requests || res.special_time_request) && (
                            <div className="mt-3 pt-3 border-t border-border">
                              {res.special_time_request && (
                                <p className="text-sm text-info bg-info/10 px-2 py-1 rounded mb-2 font-medium">
                                  ⏰ {t.requestedTime}: {res.special_time_request}
                                </p>
                              )}
                              {res.dietary_requirements && Object.keys(res.dietary_requirements).length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {Object.entries(res.dietary_requirements).map(([diet, count]) => (
                                    count > 0 && (
                                      <Badge key={diet} variant="destructive" className="text-xs">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        {count} {getDietaryLabel(diet)}
                                      </Badge>
                                    )
                                  ))}
                                </div>
                              )}
                              {res.special_requests && (
                                <p className="text-sm text-warning bg-warning/10 px-2 py-1 rounded">
                                  {t.note}: {res.special_requests}
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Room Service Modal */}
      <Dialog open={roomServiceModal.open} onOpenChange={(open) => setRoomServiceModal({ ...roomServiceModal, open })}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Bell className="w-5 h-5 text-info" />
              {t.roomService} - {roomServiceModal.mealName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {roomServiceModal.mealType && meals[roomServiceModal.mealType]?.reservations
              .filter(res => res.room_service)
              .map((res) => (
                <Card key={res.id} className="bg-background/50 border-info border-l-4">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono font-medium">{res.hotel_name}</p>
                        <p className="text-primary font-semibold text-lg">{t.cabin}: {res.cabin_name}</p>
                        <p className="text-sm text-muted-foreground">{res.guest_name}</p>
                      </div>
                      <Badge variant="secondary" className="text-lg px-3 py-1">
                        <Users className="w-4 h-4 mr-1" />
                        {res.guest_count}
                      </Badge>
                    </div>
                    {/* Dietary info if any */}
                    {res.dietary_requirements && Object.keys(res.dietary_requirements).length > 0 && 
                      Object.values(res.dietary_requirements).some(v => v > 0) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(res.dietary_requirements).map(([diet, count]) => (
                          count > 0 && (
                            <Badge key={diet} variant="destructive" className="text-xs">
                              {count} {getDietaryLabel(diet)}
                            </Badge>
                          )
                        ))}
                      </div>
                    )}
                    {res.special_requests && (
                      <p className="text-sm text-warning bg-warning/10 px-2 py-1 rounded mt-2">
                        {t.note}: {res.special_requests}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            {roomServiceModal.mealType && meals[roomServiceModal.mealType]?.reservations
              .filter(res => res.room_service).length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                {t.noRoomServiceOrders}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
